import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { BOOLEAN_MAP, BOOLEAN_OPTIONS } from 'src/features/sites/utils/constants';
import { WorkingHoursExceptionsFormSchemaValues } from 'src/features/workingHoursExceptions/utils/formSchema';
import { workingHoursMessages } from 'src/features/workingHoursExceptions/utils/messages';
import { useListZones } from 'src/features/zone/api/useListZones';
import { useParking } from 'src/providers/ParkingProvider';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import DatePickerWithTime from 'src/registry/ui/DatePickerWithTime';
import { Form, FormControl, FormControlStack, FormItem, FormLabel, FormMessage, FormField } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import { Textarea } from 'src/registry/ui/Textarea';
import { ICON_MAP } from 'src/utils/constants';
import { translate } from 'src/utils/translate';

type WorkingHoursFormProps = {
    form: UseFormReturn<WorkingHoursExceptionsFormSchemaValues>;
    onSubmit: (values: WorkingHoursExceptionsFormSchemaValues) => void;
};

function WorkingHoursExceptionsForm({ onSubmit, form }: WorkingHoursFormProps) {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();

    const { data: zoneData } = useListZones({
        parkingId,
    });

    const { all_day } = form.watch();

    const zoneOptions = React.useMemo(() => {
        if (!zoneData) return [];
        return zoneData.results.map(({ translations, id }) => ({
            label: translate(translations, locale)?.name || '',
            value: id,
        }));
    }, [zoneData, locale]);

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <SelectFormField<WorkingHoursExceptionsFormSchemaValues>
                form={form}
                name="zone"
                label={formatMessage(workingHoursMessages.zone)}
                options={zoneOptions}
            />
            <FormField
                control={form.control}
                name="date"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(workingHoursMessages.date)}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <DatePickerWithTime dateISO={field.value} onChange={field.onChange} disableTime />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <SelectFormField<WorkingHoursExceptionsFormSchemaValues>
                form={form}
                required
                name="is_open"
                isBoolean
                label={formatMessage(workingHoursMessages.isOpen)}
                options={BOOLEAN_OPTIONS}
                optionTranslationMap={BOOLEAN_MAP}
            />
            <SelectFormField<WorkingHoursExceptionsFormSchemaValues>
                form={form}
                required
                name="all_day"
                isBoolean
                label={formatMessage(workingHoursMessages.allDay)}
                options={BOOLEAN_OPTIONS}
                optionTranslationMap={BOOLEAN_MAP}
                onChange={(value) => {
                    const isAllDay = JSON.parse(value) as boolean;
                    form.setValue('start_time', isAllDay ? null : '00:00:00');
                    form.setValue('end_time', isAllDay ? null : '23:59:59');
                }}
            />
            <FormField
                control={form.control}
                name="start_time"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">{formatMessage(workingHoursMessages.timeFrame)}</FormLabel>
                        <div className="flex w-full items-center gap-4">
                            <FormControlStack>
                                <FormControl>
                                    <Input
                                        disabled={all_day}
                                        type="time"
                                        className="w-full"
                                        step={1}
                                        {...field}
                                        value={field.value || '00:00:00'}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                            <FontAwesomeIcon className="h-10 w-3 text-sm text-gray-400" icon={ICON_MAP.MINUS} />
                            <FormField
                                control={form.control}
                                name="end_time"
                                render={({ field }) => (
                                    <FormControlStack>
                                        <FormControl>
                                            <Input
                                                disabled={all_day}
                                                type="time"
                                                className="w-full"
                                                step={1}
                                                {...field}
                                                value={field.value || '23:59:59'}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormControlStack>
                                )}
                            />
                        </div>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(workingHoursMessages.description)}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Textarea
                                    {...field}
                                    placeholder={`${formatMessage(workingHoursMessages.description)}...`}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
        </Form>
    );
}

export default WorkingHoursExceptionsForm;
